import { ReactElement, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { SelectButton } from 'primereact/selectbutton'
import { Tag } from 'primereact/tag'
import {
  ColumnDTO,
  CustomTableFilterBar,
  DateFilterTemplate,
  ETypes,
  RColumn,
  RemoteTable,
  booleanFilterTemplate,
  stringFilterTemplate,
} from 'medere-ui-framework'
import ActiveFilters from '../../../components/novarium/ActiveFilters'
import { EMPTY_VALUE, formatIsoMoment, textOrDash } from '../../../components/summary/summaryFormat'
import {
  OperatingRoomFilterDTO,
  OperatingRoomOutcome,
  OperatingRoomRowDTO,
} from '../../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import { OperatingRoomTableParams, getOperatingRoomRows } from '../../../services/OperatingRoomIndicatorsController'
import { weekdayNames } from '../../ServicesStatistics/chartText'
import { formatDuration } from '../operatingRoomFormat'
import useOperatingRoomGrid, { OperatingRoomListView } from '../useOperatingRoomGrid'
import OperatingRoomGridNotice from './OperatingRoomGridNotice'

/** Clave con la que RemoteTable guarda filtros y orden en el navegador. */
export const LIST_TABLE_ID = 'OperatingRoomListTable'

/**
 * Tipo de dato de cada columna con filtro: define cómo viaja el filtro al backend. Texto: «contiene». Fecha y hora: desde /
 * hasta. Sí / No: urgente y programada. Quirófano, tipo de cirugía, cirujano, código, anestesia y financiador también son filtros
 * de «Más filtros» de la pantalla (por id): acá se buscan por su texto.
 */
const FILTERS_TYPE = [
  { field: 'patientName', type: ETypes.string },
  { field: 'documentNumber', type: ETypes.string },
  { field: 'surgeonName', type: ETypes.string },
  { field: 'roomName', type: ETypes.string },
  { field: 'surgeryTypeName', type: ETypes.string },
  { field: 'nomenclatorCode', type: ETypes.string },
  { field: 'nomenclatorName', type: ETypes.string },
  { field: 'anesthesiaName', type: ETypes.string },
  { field: 'financierName', type: ETypes.string },
  { field: 'scheduledStart', type: ETypes.dateTime },
  { field: 'urgent', type: ETypes.boolean },
  { field: 'scheduled', type: ETypes.boolean },
]

/**
 * Ancho mínimo de cada columna, en px. Son los pesos (`grow`) de las columnas y su suma es el ancho mínimo de la tabla
 * (`tableMinWidth`): si el contenedor es más angosto, la tabla se desplaza hacia el costado en vez de achicar las columnas
 * hasta truncar las cortas (fecha, agenda, duración); solo el texto largo se trunca con «…» y su tooltip.
 */
const COLUMN_WIDTH = {
  start: 144,
  room: 128,
  patient: 176,
  surgeon: 176,
  type: 144,
  code: 208,
  agenda: 144,
  urgent: 96,
  anesthesia: 144,
  financier: 160,
  duration: 96,
  outcome: 128,
  // Columna oculta (no suma al ancho de la tabla)
  document: 120,
} as const

const OUTCOMES: OperatingRoomOutcome[] = ['COMPLETED', 'ANNULLED', 'RESCHEDULED', 'MISSED', 'ALL']
const OUTCOME_LABEL_ID: Record<OperatingRoomOutcome, string> = {
  COMPLETED: 'OperatingRoomIndicatorsOutcomeCompleted',
  ANNULLED: 'OperatingRoomIndicatorsKpiAnnulled',
  RESCHEDULED: 'OperatingRoomIndicatorsKpiRescheduled',
  MISSED: 'OperatingRoomIndicatorsKpiMissed',
  ALL: 'OperatingRoomIndicatorsOutcomeAll',
}
const ROW_OUTCOME_LABEL_ID: Record<OperatingRoomRowDTO['outcome'], string> = {
  COMPLETED: 'OperatingRoomIndicatorsOutcomeCompleted',
  ANNULLED: 'OperatingRoomIndicatorsOutcomeAnnulled',
  RESCHEDULED: 'OperatingRoomIndicatorsOutcomeRescheduled',
  MISSED: 'OperatingRoomIndicatorsOutcomeMissed',
}

const DEFAULT_SORT = [{ field: 'scheduledStart', order: -1 as const }]

interface OperatingRoomListProps {
  /** Filtros aplicados de la pantalla (período y «Más filtros»). */
  filters: OperatingRoomFilterDTO
  /** Qué cirugías muestra (realizadas, anuladas…) y, si vino del mapa de calor, de qué celda. */
  view: OperatingRoomListView
  onOutcomeChange: (outcome: OperatingRoomOutcome) => void
  /** Quita la celda del mapa de calor con la que se llegó. */
  onClearCell: () => void
  /** Lo último que pidió la tabla: es lo que exporta «Exportar». */
  onLoad: (params: OperatingRoomTableParams) => void
  /** Con la acción 46: la columna Cirujano y su filtro. Sin ella no existen (tampoco se piden en el orden ni en los filtros de columna). */
  showSurgeon: boolean
}

/**
 * Lista de cirugías: `RemoteTable` con scroll infinito, filtros y orden en el backend. La clave de la fila es el id de la cita.
 * Por defecto son las realizadas; el selector de arriba pasa a las anuladas, las reprogramadas, los ausentes o todas (con la
 * columna «Estado»). «Agenda» dice si salió de una agenda (programada) o no; una cirugía urgente lleva su marca; un dato nulo,
 * «—». El DNI no se ve en la grilla: se busca con la barra «+ Filtro» (columna oculta) y sale en el Excel. Con la celda del mapa
 * de calor aparece su chip junto al selector (se quita con su ⓧ).
 */
export default function OperatingRoomList({ filters, view, onOutcomeChange, onClearCell, onLoad, showSurgeon }: OperatingRoomListProps) {
  const intl = useIntl()
  const { tableRef, params, loadItems, state, retry } = useOperatingRoomGrid<OperatingRoomRowDTO>(
    filters,
    view,
    getOperatingRoomRows,
    onLoad,
  )
  const showOutcome = view.outcome !== 'COMPLETED'
  const yesNo = useMemo(
    () => booleanFilterTemplate({ trueLabel: intl.formatMessage({ id: 'OperatingRoomIndicatorsYes' }), falseLabel: intl.formatMessage({ id: 'OperatingRoomIndicatorsNo' }) }),
    [intl],
  )
  const yesNoText = useCallback(
    (value: unknown) => intl.formatMessage({ id: value === true || value === 'true' ? 'OperatingRoomIndicatorsYes' : 'OperatingRoomIndicatorsNo' }),
    [intl],
  )
  const days = useMemo(() => weekdayNames(intl), [intl])

  const outcomeOptions = OUTCOMES.map((value) => ({ value, label: intl.formatMessage({ id: OUTCOME_LABEL_ID[value] }) }))

  /** Las columnas que se ven. El DNI también es columna de la tabla, pero queda oculto: la barra «+ Filtro» lo ofrece igual. */
  const visibleColumns = useMemo<ColumnDTO[]>(() => {
    const columns: [string, string][] = [
      ['scheduledStart', 'OperatingRoomIndicatorsColStart'],
      ['roomName', 'OperatingRoomIndicatorsColRoom'],
      ['patientName', 'OperatingRoomIndicatorsColPatient'],
      ...(showSurgeon ? [['surgeonName', 'OperatingRoomIndicatorsColSurgeon'] as [string, string]] : []),
      ['surgeryTypeName', 'OperatingRoomIndicatorsColType'],
      ['nomenclatorName', 'OperatingRoomIndicatorsColCode'],
      ['scheduled', 'OperatingRoomIndicatorsColAgenda'],
      ['urgent', 'OperatingRoomIndicatorsKpiUrgent'],
      ['anesthesiaName', 'OperatingRoomIndicatorsColAnesthesia'],
      ['financierName', 'OperatingRoomIndicatorsColFinancier'],
      ['durationMinutes', 'OperatingRoomIndicatorsColDuration'],
      ...(showOutcome ? [['outcome', 'OperatingRoomIndicatorsColOutcome'] as [string, string]] : []),
    ]
    return columns.map(([field, id]) => ({ field, header: intl.formatMessage({ id }) }))
  }, [intl, showOutcome, showSurgeon])

  // Buscador por paciente y filtros por campo. Los textos de la barra ya vienen en español en el framework
  const filterBar = useMemo<CustomTableFilterBar>(() => ({
    searchField: 'patientName',
    searchLabel: intl.formatMessage({ id: 'OperatingRoomIndicatorsColPatient' }),
    texts: { search: intl.formatMessage({ id: 'OperatingRoomIndicatorsSearchPatient' }) },
    fields: [
      { field: 'documentNumber', label: intl.formatMessage({ id: 'OperatingRoomIndicatorsColDocumentNumber' }) },
      { field: 'scheduledStart', label: intl.formatMessage({ id: 'OperatingRoomIndicatorsColStart' }) },
      ...(showSurgeon ? [{ field: 'surgeonName', label: intl.formatMessage({ id: 'OperatingRoomIndicatorsColSurgeon' }) }] : []),
      { field: 'roomName', label: intl.formatMessage({ id: 'OperatingRoomIndicatorsColRoom' }) },
      { field: 'surgeryTypeName', label: intl.formatMessage({ id: 'OperatingRoomIndicatorsColType' }) },
      { field: 'nomenclatorCode', label: intl.formatMessage({ id: 'OperatingRoomIndicatorsColCodeNumber' }) },
      { field: 'nomenclatorName', label: intl.formatMessage({ id: 'OperatingRoomIndicatorsColCodeName' }) },
      { field: 'anesthesiaName', label: intl.formatMessage({ id: 'OperatingRoomIndicatorsColAnesthesia' }) },
      { field: 'financierName', label: intl.formatMessage({ id: 'OperatingRoomIndicatorsColFinancier' }) },
      { field: 'scheduled', label: intl.formatMessage({ id: 'OperatingRoomIndicatorsKpiScheduled' }), format: yesNoText },
      { field: 'urgent', label: intl.formatMessage({ id: 'OperatingRoomIndicatorsKpiUrgent' }), format: yesNoText },
    ],
  }), [intl, yesNoText, showSurgeon])

  /** Datos largos: truncados con «…» y el texto completo en el tooltip. */
  const textCell = (value: string | null | undefined): ReactElement | string => {
    const text = textOrDash(value ?? null)
    return text === EMPTY_VALUE ? text : <span className="nv-truncate nv-tip" data-pr-tooltip={text}>{text}</span>
  }

  const codeText = (row: OperatingRoomRowDTO): string => [row.nomenclatorCode, row.nomenclatorName].filter(Boolean).join(' · ')

  const tableMinWidth = Object.entries(COLUMN_WIDTH)
    .filter(([key]) => key !== 'document' && (showOutcome || key !== 'outcome') && (showSurgeon || key !== 'surgeon'))
    .reduce((sum, [, width]) => sum + width, 0)

  const cellChips = view.cell
    ? [{
      key: 'cell',
      label: intl.formatMessage({ id: 'OperatingRoomIndicatorsCellChip' }, {
        day: days[view.cell.dayOfWeek - 1] ?? '',
        hour: `${String(view.cell.hour).padStart(2, '0')}:00`,
        measure: intl.formatMessage({ id: view.cell.measure === 'HOURS' ? 'OperatingRoomIndicatorsHeatHours' : 'OperatingRoomIndicatorsHeatStarted' }),
      }),
    }]
    : []

  return (
    <div className="nv-or-grid">
      <div className="nv-or-toolbar">
        <SelectButton
          value={view.outcome}
          options={outcomeOptions}
          optionLabel="label"
          optionValue="value"
          onChange={(event) => { if (event.value) onOutcomeChange(event.value as OperatingRoomOutcome) }}
          aria-label={intl.formatMessage({ id: 'OperatingRoomIndicatorsOutcomeBy' })}
        />
        <ActiveFilters chips={cellChips} onRemove={onClearCell} onClear={onClearCell} />
      </div>
      <OperatingRoomGridNotice state={state} errorMessageId="OperatingRoomIndicatorsListError" onRetry={retry} />
      <div className="nv-or-table">
        <RemoteTable
          ref={tableRef}
          idTable={LIST_TABLE_ID}
          dataKey="appointmentId"
          getItems={loadItems}
          params={params}
          paramsDelay={0}
          filterBar={filterBar}
          filtersType={FILTERS_TYPE}
          columnsVisible={visibleColumns}
          defaultMultiSortMeta={DEFAULT_SORT}
          refreshButton
          resizableColumns
          tableMinWidth={tableMinWidth}
          // Con error el cuerpo no dice «No hay cirugías»: lo que pasó lo dice el aviso de arriba
          notFoundText={state === 'ok' ? intl.formatMessage({ id: 'OperatingRoomIndicatorsListEmpty' }) : ' '}
          minHeight={480}
        >
          <RColumn
            field="scheduledStart"
            header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColStart' })}
            grow={COLUMN_WIDTH.start}
            sortable
            filter
            filterElement={DateFilterTemplate}
            body={(row: OperatingRoomRowDTO) => formatIsoMoment(row.scheduledStart)}
          />
          <RColumn
            field="roomName"
            header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColRoom' })}
            grow={COLUMN_WIDTH.room}
            sortable
            filter
            filterElement={stringFilterTemplate}
            body={(row: OperatingRoomRowDTO) => textCell(row.roomName)}
          />
          <RColumn
            field="patientName"
            header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColPatient' })}
            grow={COLUMN_WIDTH.patient}
            sortable
            filter
            filterElement={stringFilterTemplate}
            body={(row: OperatingRoomRowDTO) => textCell(row.patientName)}
          />
          {showSurgeon && (
            <RColumn
              field="surgeonName"
              header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColSurgeon' })}
              grow={COLUMN_WIDTH.surgeon}
              sortable
              filter
              filterElement={stringFilterTemplate}
              body={(row: OperatingRoomRowDTO) => textCell(row.surgeonName)}
            />
          )}
          <RColumn
            field="surgeryTypeName"
            header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColType' })}
            grow={COLUMN_WIDTH.type}
            sortable
            filter
            filterElement={stringFilterTemplate}
            body={(row: OperatingRoomRowDTO) => textCell(row.surgeryTypeName)}
          />
          <RColumn
            field="nomenclatorName"
            header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColCode' })}
            grow={COLUMN_WIDTH.code}
            sortable
            filter
            filterElement={stringFilterTemplate}
            body={(row: OperatingRoomRowDTO) => textCell(codeText(row))}
          />
          <RColumn
            field="scheduled"
            header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColAgenda' })}
            grow={COLUMN_WIDTH.agenda}
            sortable
            filter
            filterElement={yesNo}
            body={(row: OperatingRoomRowDTO) => intl.formatMessage({ id: row.scheduled ? 'OperatingRoomIndicatorsAgendaScheduled' : 'OperatingRoomIndicatorsAgendaOut' })}
          />
          <RColumn
            field="urgent"
            header={intl.formatMessage({ id: 'OperatingRoomIndicatorsKpiUrgent' })}
            grow={COLUMN_WIDTH.urgent}
            sortable
            filter
            filterElement={yesNo}
            body={(row: OperatingRoomRowDTO) => (row.urgent
              ? <Tag className="nv-tag-neutral" value={intl.formatMessage({ id: 'OperatingRoomIndicatorsUrgentTag' })} />
              : EMPTY_VALUE)}
          />
          <RColumn
            field="anesthesiaName"
            header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColAnesthesia' })}
            grow={COLUMN_WIDTH.anesthesia}
            sortable
            filter
            filterElement={stringFilterTemplate}
            body={(row: OperatingRoomRowDTO) => textCell(row.anesthesiaName)}
          />
          <RColumn
            field="financierName"
            header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColFinancier' })}
            grow={COLUMN_WIDTH.financier}
            sortable
            filter
            filterElement={stringFilterTemplate}
            body={(row: OperatingRoomRowDTO) => textCell(row.financierName)}
          />
          <RColumn
            field="durationMinutes"
            header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColDuration' })}
            grow={COLUMN_WIDTH.duration}
            className="nv-urg-center"
            headerClassName="nv-urg-center"
            body={(row: OperatingRoomRowDTO) => formatDuration(intl, row.durationMinutes)}
          />
          {showOutcome && (
            <RColumn
              field="outcome"
              header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColOutcome' })}
              grow={COLUMN_WIDTH.outcome}
              sortable
              body={(row: OperatingRoomRowDTO) => intl.formatMessage({ id: ROW_OUTCOME_LABEL_ID[row.outcome] })}
            />
          )}
          {/* Oculta (ver `visibleColumns`): solo para buscar y filtrar */}
          <RColumn
            field="documentNumber"
            header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColDocumentNumber' })}
            grow={COLUMN_WIDTH.document}
            filter
            filterElement={stringFilterTemplate}
          />
        </RemoteTable>
      </div>
    </div>
  )
}
